// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import {
  dappAPI,
  listAccounts,
  onAccountsChanged,
  removeOnAccountsChanged,
} from '@canton-network/dapp-sdk';
import { useEffect, useState } from 'react';

export const useAccounts = (isConnected: boolean = false) => {
  const [accounts, setAccounts] = useState<dappAPI.Wallet[]>();

  useEffect(() => {
    // 1. GUARD CLAUSE: Handle the "Disconnected" state first.
    // If not connected, clear data and stop.
    // We do NOT subscribe, so we don't need to return a cleanup function here.
    if (!isConnected) {
      setAccounts(undefined);
      return () => {};
    }

    // =========================================================
    // CODE BELOW THIS LINE ONLY RUNS IF CONNECTED
    // =========================================================

    let isActive = true;

    // 2. Define Listener
    const listener = (event: dappAPI.AccountsChangedEvent) => {
      if (isActive) setAccounts(event);
    };

    // 3. Subscribe (Now strictly scoped to connected state)
    onAccountsChanged(listener).catch(console.error);

    // 4. Initial Fetch
    listAccounts()
      .then((wallets) => {
        if (isActive) setAccounts(wallets);
      })
      .catch((err) => {
        console.error('Error requesting wallets:', err);
      });

    // 5. Cleanup
    // React will run this function when the component unmounts OR
    // before the effect runs again (e.g., if isConnected becomes false).
    return () => {
      isActive = false;
      removeOnAccountsChanged(listener).catch(console.error);
    };
  }, [isConnected]);

  return accounts;
};
